import React, { useCallback, useEffect } from 'react';
import { HelpMessage, ListView, ListViewActions, Page } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useActions, useDocumentTitle } from '~/hooks';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import CostPeriodRow from './CostPeriodRow';

const initialState = { isReady: false, data: null };
const handlers = {
  ready: ({ data }) => ({ isReady: true, dialog: null, data, editIndex: null }),
  edit: (editIndex) => ({ editIndex }),
};

function CostPeriods({ member, onSaved }) {
  useDocumentTitle(`${member.name} Cost`);

  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ isReady, data, editIndex }, actions] = useActions(handlers, initialState);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).members(member.id).costPeriods().get();

      actions.ready({ data });
    } catch (error) {
      actions.ready({ data: null });
    }
  }, [actions, workspace.id, member.id, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = async () => {
    await fetchData();
    await onSaved();
  };

  if (!isReady && !data) return <PageLoader />;
  if (!data) return <ErrorPage publicSite={false} />;
  return (
    <>
      <HelpMessage spaceTop>
        For workers paid hourly, use the Hourly Wage cost method. For workers paid a fixed amount, use either the
        per-month or per-hour Fixed Wage cost method.
      </HelpMessage>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column minWidth="16rem">Date Range</ListView.Column>
            <ListView.Column minWidth="18rem">Currency</ListView.Column>
            <ListView.Column minWidth="20rem">Cost Method</ListView.Column>
            <ListView.Column width="12rem" align="right">
              Labor Cost
            </ListView.Column>
            <ListView.Column width="12rem" align="right">
              Overhead Cost
            </ListView.Column>
            <ListView.Column width="10rem" align="right">
              Total Cost
            </ListView.Column>
            <ListView.Column width="10rem" align="right">
              Days in Period
            </ListView.Column>
            <ListViewActions.Column />
          </ListView.Header>

          <ListView.Body>
            {data.map((item, index) => (
              <CostPeriodRow
                key={item.id}
                period={item}
                periods={data}
                member={member}
                disableActions={editIndex !== null}
                isEditing={editIndex === index}
                onEdit={() => actions.edit(index)}
                onCancel={() => actions.edit(null)}
                onSaved={handleChange}
                onDeleted={handleChange}
              />
            ))}

            <CostPeriodRow
              period={{}}
              periods={data}
              member={member}
              isEditing={editIndex === -1}
              disableActions={editIndex !== null}
              onEdit={() => actions.edit(-1)}
              onCancel={() => actions.edit(null)}
              onSaved={handleChange}
            />
          </ListView.Body>

          <ListView.Status total={data.length} label="Cost Period" />
        </ListView>
      </Page.ListView>
    </>
  );
}

export default CostPeriods;
